import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  Layers3,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';

const DEFAULT_CLASSIFICATIONS = [
  { code: 'produce', name: 'Produce' },
  { code: 'short_life', name: 'Short Life' },
  { code: 'dairy', name: 'Dairy' },
  { code: 'liquor', name: 'Liquor' },
  { code: 'frozen', name: 'Frozen' },
];

const getTypeCode = (entry) => entry?.code || entry?.classification_code || '';
const getTypeName = (entry) => entry?.name || entry?.classification_name || getTypeCode(entry);

const CategoryClassificationManagement = () => {
  const { hasFeatureAccess } = useAuthStore();
  const canManage = hasFeatureAccess('admin.category_classifications');
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const vendorId = selectedStore?.vendorId || selectedStore?.vendor_id || null;

  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [removingId, setRemovingId] = useState('');
  const [types, setTypes] = useState(DEFAULT_CLASSIFICATIONS);
  const [mappings, setMappings] = useState([]);
  const [activeCode, setActiveCode] = useState('produce');
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [includeChildren, setIncludeChildren] = useState(true);
  const [pendingRemove, setPendingRemove] = useState(null);

  const normalizedTypes = useMemo(() => {
    const source = types.length ? types : DEFAULT_CLASSIFICATIONS;
    return source.map((entry) => ({
      code: getTypeCode(entry),
      name: getTypeName(entry),
      description: entry.description || '',
    })).filter((entry) => entry.code);
  }, [types]);

  const activeType = useMemo(
    () => normalizedTypes.find((entry) => entry.code === activeCode) || normalizedTypes[0] || DEFAULT_CLASSIFICATIONS[0],
    [activeCode, normalizedTypes]
  );

  const visibleMappings = useMemo(
    () => mappings.filter((entry) => entry.classificationCode === activeCode && entry.isActive !== false),
    [activeCode, mappings]
  );

  const mappedCategoryIds = useMemo(
    () => new Set(visibleMappings.map((entry) => String(entry.categoryId))),
    [visibleMappings]
  );

  const loadPage = useCallback(async () => {
    if (!canManage || !vendorId) return;
    setLoading(true);
    try {
      const response = await adminAPI.getCategoryClassifications({ vendorId });
      const payload = response.data?.data || {};
      setTypes(payload.types?.length ? payload.types : DEFAULT_CLASSIFICATIONS);
      setMappings(payload.mappings || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load category classifications');
    } finally {
      setLoading(false);
    }
  }, [canManage, vendorId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!canManage || !vendorId) {
      setSearchResults([]);
      setSearchPerformed(false);
      setPagination({ page: 1, limit: 25, total: 0, pages: 0 });
      return;
    }

    let cancelled = false;

    const loadAvailableCategories = async () => {
      setSearch('');
      setSearchLoading(true);
      setSearchPerformed(true);
      try {
        const response = await adminAPI.searchCategoryClassificationCategories({
          vendorId,
          classificationCode: activeCode,
          search: '',
          page: 1,
          limit: pagination.limit || 25,
        });

        if (cancelled) return;
        setSearchResults(response.data?.data || []);
        setPagination(response.data?.pagination || { page: 1, limit: 25, total: 0, pages: 0 });
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || 'Failed to load categories');
          setSearchResults([]);
          setPagination({ page: 1, limit: 25, total: 0, pages: 0 });
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    };

    loadAvailableCategories();

    return () => {
      cancelled = true;
    };
  }, [activeCode, canManage, pagination.limit, vendorId]);

  const searchCategories = async (nextPage = 1) => {
    if (!vendorId) {
      toast.error('Select a store first');
      return;
    }

    setSearchLoading(true);
    setSearchPerformed(true);
    try {
      const response = await adminAPI.searchCategoryClassificationCategories({
        vendorId,
        classificationCode: activeCode,
        search,
        page: nextPage,
        limit: pagination.limit || 25,
      });
      setSearchResults(response.data?.data || []);
      setPagination(response.data?.pagination || { page: nextPage, limit: 25, total: 0, pages: 0 });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to search categories');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddCategory = async (category) => {
    const categoryId = category.categoryId;
    if (!categoryId || !vendorId) return;

    setSavingId(String(categoryId));
    try {
      const response = await adminAPI.saveCategoryClassification({
        vendorId,
        categoryId,
        classificationCode: activeCode,
        includeChildCategories: includeChildren,
      });
      const nextMapping = response.data?.data;
      if (nextMapping?.id) {
        setMappings((prev) => [
          nextMapping,
          ...prev.filter((entry) => String(entry.id) !== String(nextMapping.id)),
        ]);
      }
      toast.success(`${category.categoryName} added to ${activeType.name}`);
      await searchCategories(pagination.page || 1);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add category');
    } finally {
      setSavingId('');
    }
  };

  const handleRemoveCategory = async () => {
    const mapping = pendingRemove;
    if (!mapping?.id) return;

    setRemovingId(String(mapping.id));
    try {
      await adminAPI.deleteCategoryClassification(mapping.id);
      setMappings((prev) => prev.filter((entry) => String(entry.id) !== String(mapping.id)));
      toast.success('Category removed');
      setPendingRemove(null);
      if (searchPerformed) {
        await searchCategories(pagination.page || 1);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove category');
    } finally {
      setRemovingId('');
    }
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Category Classifications</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Category Classifications</h1>
          <p className="mt-1 text-sm text-gray-500">
            {selectedStore?.name || 'No store selected'}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary inline-flex items-center justify-center gap-2"
          onClick={loadPage}
          disabled={loading || !vendorId}
        >
          <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {!vendorId ? (
        <div className="card">
          <p className="text-sm text-slate-500">Select a store context from the header.</p>
        </div>
      ) : (
        <>
          <div className="card space-y-4">
            <div className="flex flex-wrap gap-2">
              {normalizedTypes.map((entry) => {
                const isActive = activeCode === entry.code;
                const count = mappings.filter((mapping) => mapping.classificationCode === entry.code && mapping.isActive !== false).length;
                return (
                  <button
                    key={entry.code}
                    type="button"
                    onClick={() => setActiveCode(entry.code)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-primary-200 bg-primary-50 text-primary-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Layers3 className="h-4 w-4" />
                    <span>{entry.name}</span>
                    <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs text-slate-500">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto_auto] lg:items-center">
              <label className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  className="input pl-9"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') searchCategories(1);
                  }}
                  placeholder={`Search ${activeType.name} categories`}
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-primary-600"
                  checked={includeChildren}
                  onChange={(event) => setIncludeChildren(event.target.checked)}
                />
                Include child categories
              </label>
              <button
                type="button"
                className="btn-primary inline-flex items-center justify-center gap-2"
                onClick={() => searchCategories(1)}
                disabled={searchLoading}
              >
                <Search className="h-4 w-4" />
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
            <div className="card space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Search Results</h2>
                  <p className="text-sm text-slate-500">
                    {searchPerformed ? `${pagination.total || 0} categories found` : 'Available categories load automatically'}
                  </p>
                </div>
              </div>

              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {searchLoading ? (
                  <div className="p-4 text-sm text-slate-500">Searching categories...</div>
                ) : searchResults.length ? (
                  searchResults.map((category) => {
                    const alreadyMapped = mappedCategoryIds.has(String(category.categoryId));
                    return (
                      <div key={category.categoryId} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{category.categoryName}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {category.parentCategoryName || 'Root category'} | ID {category.categoryId}
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                            alreadyMapped
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                          }`}
                          onClick={() => handleAddCategory(category)}
                          disabled={alreadyMapped || savingId === String(category.categoryId)}
                        >
                          {alreadyMapped ? <CheckCircle2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                          {alreadyMapped ? 'Added' : (savingId === String(category.categoryId) ? 'Adding...' : 'Add')}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-sm text-slate-500">
                    {searchPerformed ? 'No matching categories.' : 'Loading available categories...'}
                  </div>
                )}
              </div>

              {pagination.pages > 1 && (
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={searchLoading || pagination.page <= 1}
                    onClick={() => searchCategories((pagination.page || 1) - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-500">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={searchLoading || pagination.page >= pagination.pages}
                    onClick={() => searchCategories((pagination.page || 1) + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            <div className="card space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{activeType.name} Categories</h2>
                <p className="text-sm text-slate-500">{visibleMappings.length} active mappings</p>
              </div>

              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {loading ? (
                  <div className="p-4 text-sm text-slate-500">Loading mappings...</div>
                ) : visibleMappings.length ? (
                  visibleMappings.map((mapping) => (
                    <div key={mapping.id} className="flex items-start justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{mapping.categoryName || `Category ${mapping.categoryId}`}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {mapping.parentCategoryName || 'Root category'} | ID {mapping.categoryId}
                        </p>
                        {mapping.includeChildCategories && (
                          <p className="mt-1 text-xs text-primary-600">Child categories included</p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                        onClick={() => setPendingRemove(mapping)}
                        disabled={removingId === String(mapping.id)}
                        title="Remove category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-sm text-slate-500">No categories mapped.</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {pendingRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900">Remove Category</h3>
            <p className="mt-2 text-sm text-slate-600">
              Remove "{pendingRemove.categoryName || `Category ${pendingRemove.categoryId}`}" from {activeType.name}?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPendingRemove(null)}
                disabled={Boolean(removingId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-60"
                onClick={handleRemoveCategory}
                disabled={Boolean(removingId)}
              >
                {removingId ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryClassificationManagement;
